/**
 * 预算表单（**G 域**）—— 新建 / 编辑（**受控**：状态全部在页面里）
 *
 * 依据：G 域 PRD **§4.7**（三档建议）· **§4.3**（软提示）· `AC-G1.3` / `AC-G1.15` ~ `AC-G1.20` /
 * `AC-G1.28`。
 *
 * ## 🔴 三段式（与 ㉛ / ㉝ / ㊸ 批一致）
 *
 * | 层 | 文件 | 管什么 |
 * |---|---|---|
 * | 纯逻辑 | `features/budget-draft.ts` | 草稿 → 请求、校验（**调内核**，文案与服务端 400 逐字相同）、采纳建议 |
 * | 呈现 | 本文件 | 长什么样、怎么交互 |
 * | 页面 | `pages/PlanPage.tsx` | 组装 + 请求 + 缓存失效 |
 *
 * ## 🔴 四条刻意决定
 *
 * 1. **不用 `Portal`** —— 静态渲染要能断言（㉝ 批纪律），所以是普通 `Card` 而不是 `Dialog`；
 * 2. **分类选择复用 `CategoryPicker`**（`kind='expense'`）—— 收入分类**不进预算入口**（`DR-011` §8-3），
 *    由该组件的 `kind` 过滤保证，本文件不自己过滤；
 * 3. **三档建议的某一档为 `null` ⇒ 该按钮不渲染**（不是显示 `¥0.00` —— `AC-G1.18`）；
 * 4. **软提示只提示不拦截**（`AC-G1.28`）：`issue === null` 即可保存，超合计只加一条提示条。
 */

import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import {
  BudgetPeriod,
  BudgetSuggestionTier,
  type BudgetSuggestionView,
  type CategoryView,
} from '@jiaying/shared';
import { Amount } from './Amount.js';
import { CategoryPicker } from './CategoryPicker.js';
import {
  PERIOD_LABELS,
  SUGGESTION_LABELS,
  SUGGESTION_TITLE_CATEGORY,
  SUGGESTION_TITLE_TOTAL,
  SOFT_HINT_LEAD,
  TOTAL_BELOW_SUM_HINT,
} from '../features/budget-labels.js';
import {
  TOTAL_OPTION_ID,
  applySuggestion,
  type BudgetDraft,
} from '../features/budget-draft.js';
import type { BudgetSoftHint } from '../features/budget-draft.js';

/**
 * 🔴 `BudgetSoftHint` 的**唯一定义**在纯逻辑层（`features/budget-draft.ts`）——
 * 本文件只 import + 转出（批 `109` 把它从页面搬到纯逻辑层后，这里不再留第二份）。
 */
export type { BudgetSoftHint } from '../features/budget-draft.js';

export interface BudgetFormProps {
  mode: 'create' | 'edit';
  draft: BudgetDraft;
  onChange: (next: BudgetDraft) => void;
  /** 全部分类（`CategoryPicker` 自己按 `kind` 过滤；本组件用它查所选分类） */
  categories: readonly CategoryView[];
  /** 服务端下发的三档建议（`AC-G1.15`；`undefined` = 还在取 / 取失败 ⇒ 不显示建议区） */
  suggestion: BudgetSuggestionView | undefined;
  /** 纯逻辑层给出的第一处问题（`null` = 可保存） */
  issue: string | null;
  /** 软提示数据（`overAllocated` 为真时才提示；**仍可保存**） */
  softHint: BudgetSoftHint | null;
  currency: string;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

export function BudgetForm(props: BudgetFormProps) {
  const { draft, onChange, categories, suggestion, issue, softHint, currency, saving } = props;
  const isEvent = draft.period === BudgetPeriod.Event;

  return (
    <Card data-testid="budget-form">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {props.mode === 'create' ? '新建预算' : '编辑预算'}
        </Typography>

        {/* ── 分类（总预算走占位键；分类走 `CategoryPicker`）── */}
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant={draft.categoryId === TOTAL_OPTION_ID ? 'contained' : 'outlined'}
              onClick={() => onChange({ ...draft, categoryId: TOTAL_OPTION_ID })}
              data-testid="pick-total"
            >
              总预算
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              或
            </Typography>
          </Stack>

          {/* 🔴 `kind='expense'` ⇒ 收入分类**不会**出现在选项里（`DR-011` §8-3） */}
          <Box data-testid="budget-category-picker">
            <CategoryPicker
              kind="expense"
              items={categories}
              value={draft.categoryId === TOTAL_OPTION_ID ? null : draft.categoryId || null}
              onChange={(id) => onChange({ ...draft, categoryId: id ?? '' })}
              label="分类"
            />
          </Box>

          {/* ── 周期（四档；`event` 才要起止）── */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} data-testid="period-picker">
            {[BudgetPeriod.Monthly, BudgetPeriod.Quarterly, BudgetPeriod.Yearly, BudgetPeriod.Event].map(
              (p) => (
                <Button
                  key={p}
                  size="small"
                  variant={draft.period === p ? 'contained' : 'outlined'}
                  onClick={() => onChange({ ...draft, period: p })}
                  data-testid={`period-${p}`}
                >
                  {PERIOD_LABELS[p]}
                </Button>
              ),
            )}
          </Stack>

          {isEvent ? (
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                type="date"
                label="开始"
                value={draft.startDate}
                onChange={(e) => onChange({ ...draft, startDate: e.target.value })}
                inputProps={{ 'data-testid': 'budget-start' }}
              />
              <TextField
                size="small"
                type="date"
                label="结束"
                value={draft.endDate}
                onChange={(e) => onChange({ ...draft, endDate: e.target.value })}
                inputProps={{ 'data-testid': 'budget-end' }}
              />
            </Stack>
          ) : null}

          <TextField
            size="small"
            label="额度（元）"
            value={draft.amountText}
            onChange={(e) => onChange({ ...draft, amountText: e.target.value })}
            inputProps={{ 'data-testid': 'budget-amount', inputMode: 'decimal' }}
          />
        </Stack>

        {/* ── 三档建议（`AC-G1.16` / `AC-G1.17`：默认推荐 = 近 3 期均值）── */}
        {suggestion === undefined ? null : (
          <Box sx={{ mt: 1.5 }} data-testid="budget-suggestions">
            <Typography variant="caption" color="text.secondary" data-testid="budget-suggestions-title">
              {/* 🆕 批 `109`：总预算的参照是"全部支出"，分类预算是"这个分类" */}
              {draft.categoryId === TOTAL_OPTION_ID ? SUGGESTION_TITLE_TOTAL : SUGGESTION_TITLE_CATEGORY}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
              {(
                [
                  BudgetSuggestionTier.RecentAverage,
                  BudgetSuggestionTier.LastWindow,
                  BudgetSuggestionTier.IncomeShare,
                ] as const
              ).map((tier) => {
                const cents = suggestion[tier];
                // 🔴 `null` ⇒ **该档隐藏**（`AC-G1.18`：无收入就藏第 3 档，而不是显示 ¥0）
                if (cents === null) return null;
                const recommended = suggestion.recommended === tier;
                return (
                  <Button
                    key={tier}
                    size="small"
                    variant={recommended ? 'contained' : 'outlined'}
                    color={recommended ? 'primary' : 'inherit'}
                    onClick={() => props.onChange(applyTier(draft, suggestion, tier))}
                    data-testid={`suggest-${tier}`}
                  >
                    {SUGGESTION_LABELS[tier]}{' '}
                    <Amount cents={cents} currency={currency} variant="caption" colored={false} />
                  </Button>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* ── 软提示（`AC-G1.28`：**两个具体数字** + 仍可保存）── */}
        {softHint === null ? null : softHint.kind === 'totalBelowSum' ? (
          // 🆕 批 `109`：反方向 —— 把**总预算**调到低于分类预算之和
          <Alert severity="info" sx={{ mt: 1.5 }} data-testid="budget-soft-hint">
            {SOFT_HINT_LEAD}{' '}
            <Amount cents={softHint.categorySum} currency={currency} variant="caption" colored={false} />{' '}
            {TOTAL_BELOW_SUM_HINT}{' '}
            <Amount cents={softHint.totalAmount} currency={currency} variant="caption" colored={false} />
            。<b>仍可保存</b> —— 这是软提示、不拦你。
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mt: 1.5 }} data-testid="budget-soft-hint">
            {SOFT_HINT_LEAD}{' '}
            <Amount cents={softHint.categorySumWithNew} currency={currency} variant="caption" colored={false} />{' '}
            已超过总预算{' '}
            <Amount cents={softHint.totalAmount} currency={currency} variant="caption" colored={false} />
            （当前合计{' '}
            <Amount cents={softHint.categorySum} currency={currency} variant="caption" colored={false} />
            ）。<b>仍可保存</b> —— 这是软提示、不拦你。
          </Alert>
        )}

        {issue === null ? null : (
          <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="budget-issue">
            {issue}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            variant="contained"
            disabled={issue !== null || saving}
            onClick={props.onSave}
            data-testid="budget-save"
          >
            保存
          </Button>
          <Button onClick={props.onCancel} data-testid="budget-cancel">
            取消
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** 点某一档 ⇒ 金额文本跟着变（**纯逻辑在 `budget-draft.ts`**，此处只转接） */
function applyTier(
  draft: BudgetDraft,
  suggestion: BudgetSuggestionView,
  tier: BudgetSuggestionTier,
): BudgetDraft {
  return applySuggestion(draft, tier, suggestion);
}
